import React, { useCallback, useMemo, useState } from "react";
import { Conditions } from "../conditions";
import { MenuItem, Select } from "@mui/material";
import { SelectWithSearchAndFilters } from "shared/components/Inputs/SelectWithSearchAndFilters";
import { AllowedBlockTypes, ConditionOperatorTypeTitles, ConditionOperatorTypes } from "../helpers/conditions";
import { BlockTypes } from "modules/MainSurveyEditor/helpers/blocks";
import { useSurveysData } from "modules/Surveys/hooks/useSurveysData";
import { MainSurveyStore } from "modules/MainSurveyEditor/hooks/useMainSurvey";
import { get } from "lodash";
import { BlockNestedKey, Blockable } from "modules/MainSurveyEditor/helpers/blockTree";

export const ConditionInput = (props: ConditionInputProps) => {
  const {
    mainSurveyStore: { mainSurvey, mainSurveyTree, getAndSetNested },
    condition,
    onUpdate,
    selectPath,
    setSelectPath,
    selectGoBackAction,
  } = props;

  const { surveys, isLoading: isParentLoading } = useSurveysData();

  const [parentValue, setParentValue] = useState<string>(String(mainSurvey!.id));
  const [blockFilter, setBlockFilter] = useState<string>("");
  const [isOptionsLoading, setIsOptionsLoading] = useState<boolean>(false);

  const handleGetAndSetNested = useCallback(
    async (path: string) => {
      const block = get(mainSurveyTree, path) as Blockable;
      if (block[BlockNestedKey]?.length) {
        return;
      }

      setIsOptionsLoading(true);
      await getAndSetNested(block);
      setIsOptionsLoading(false);
    },
    [getAndSetNested, mainSurveyTree]
  );

  const options = useMemo(() => {
    if (isOptionsLoading) {
      return [];
    }

    const parent = (get(mainSurveyTree, selectPath) || mainSurveyTree) as Blockable;

    return (
      parent[BlockNestedKey]?.map((block, i) => {
        const functions = {} as any;
        if (block.blockType.includes(BlockTypes.Survey)) {
          functions.onExpand = () => {
            const newPath = `${selectPath}["${BlockNestedKey}"][${i}]`;
            setSelectPath(newPath);
            handleGetAndSetNested(newPath);
          };
        } else {
          functions.onSelect = () =>
            onUpdate({
              objectId: block.objectId,
              value: block.options![0],
              operatorType: condition.operatorType || ConditionOperatorTypes.Is,
              actionType: condition.actionType,
              contentObject: { ...condition.contentObject, options: block.options, title: block.title },
              contentType: block.blockType,
            });
        }

        return {
          label: block.title,
          value: String(block.objectId),
          blockType: block.blockType,
          ...functions,
        };
      }).filter(
        (block) =>
          (AllowedBlockTypes.includes(block.blockType) && block.blockType.includes(blockFilter)) ||
          block.blockType.includes(BlockTypes.Survey)
      ) || []
    );
  }, [
    isOptionsLoading,
    mainSurveyTree,
    selectPath,
    setSelectPath,
    handleGetAndSetNested,
    onUpdate,
    condition,
    blockFilter,
  ]);

  const isOperatorIsFullWidth = condition.operatorType === ConditionOperatorTypes.IsEmpty || condition.operatorType === ConditionOperatorTypes.IsNotEmpty;
 
  const selectBlockableFilters = [
    {
      label: "Single select",
      value: BlockTypes.SingleSelect,
      onSelect: setBlockFilter,
    },
    {
      label: "Multi select",
      value: BlockTypes.MultiSelect,
      onSelect: setBlockFilter,
    },
    {
      label: "None",
      value: "",
      onSelect: setBlockFilter,
    },
  ];

  return (
    <div className="module-condition-input">
      <SelectWithSearchAndFilters
        selectedOption={{ label: condition.contentObject?.title || "", value: String(condition.objectId || ""), blockType: condition.contentType }}
        selectedFilter={blockFilter}
        parentValue={parentValue}
        isOptionsLoading={isOptionsLoading}
        isParentLoading={isParentLoading}
        options={options}
        parentOptions={surveys.map((s) => ({ label: s.title, value: String(s.id) }))}
        handleParentChange={setParentValue}
        filterOptions={selectBlockableFilters}
        goBackAction={selectGoBackAction}
        className="full-width"
        placeholder=" Select a question"
      />

      <Select
        value={condition.operatorType || ConditionOperatorTypes.Is}
        onChange={(e) => onUpdate({ operatorType: e.target.value as ConditionOperatorTypes })}
        disabled={!condition.objectId}
        className={isOperatorIsFullWidth ? "full-width" : ""}
      >
        {Object.values(ConditionOperatorTypes).map((operatorType) => (
          <MenuItem key={operatorType} value={operatorType}>
            {ConditionOperatorTypeTitles[operatorType]}
          </MenuItem>
        ))}
      </Select>

      {!isOperatorIsFullWidth && (
        <Select
          value={condition.value || ""}
          onChange={(e) => onUpdate({ value: e.target.value })}
          disabled={!condition.objectId}
        >
          {(condition.contentObject?.options || []).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      )}
    </div>
  );
};

export interface ConditionInputProps {
  mainSurveyStore: MainSurveyStore;
  condition: Partial<Conditions.Condition>;
  onUpdate: (conditionUpdate: Partial<Conditions.Condition>) => void;
  valueOptions: string[];
  selectPath: string;
  setSelectPath: (path: string) => void;
  selectGoBackAction?: { onClick: () => void; title: string };
}
